import { useEffect, useState, type ReactNode } from "react";
import { SeasonContext, type SeasonContextType } from "./SeasonContext";
import type { SeasonCheckboxes } from "../utils/types";
import {
  ApplicationPeriod,
  callFetchSeason,
  callJoinSeasonWaitlist,
  callUpdateSeason,
  type Season,
} from "../../../api/season";

interface SeasonContextProviderProps {
  children: ReactNode;
}
const initialCheckboxes: SeasonCheckboxes = {
  printHarvestLogs: false,
  printOrders: false,
  updatePickupLocations: false,
  updateSubscriptionsAndTiers: false,
  updateCsaDeliveryPrice: false,
};

export type SeasonCheckboxKey =
  | "printHarvestLogs"
  | "printOrders"
  | "updatePickupLocations"
  | "updateSubscriptionsAndTiers"
  | "updateCsaDeliveryPrice";

export const SeasonContextProvider = ({
  children,
}: SeasonContextProviderProps) => {
  const [season, setSeason] = useState<Season | null>(null);
  const [areWeInSeason, setAreWeInSeason] = useState(false);
  const [isBusinessOpen, setIsBusinessOpen] = useState(false);
  const [applicationPeriod, setApplicationPeriod] = useState<ApplicationPeriod>(
    ApplicationPeriod.ApplicationClosed,
  );
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState(1000);
  const [businessDeliveryPrice, setBusinessDeliveryPrice] = useState(80);
  const [csaDeliveryPrice, setCSADeliveryPrice] = useState(80);
  const [seasonPreviewOverride, setSeasonPreviewOverride] = useState<
    boolean | null
  >(null); //PREVIEW only
  const [
    applicationPeriodPreviewOverride,
    setApplicationPeriodPreviewOverride,
  ] = useState<ApplicationPeriod | null>(null); //PREVIEW only
  const [checkboxes, setCheckboxes] =
    useState<SeasonCheckboxes>(initialCheckboxes);
  const [waitlist, setWaitlist] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applySeasonData = (seasonData: Season): void => {
    setSeason(seasonData);
    setAreWeInSeason(seasonData.areWeInSeason ?? false);
    setIsBusinessOpen(seasonData.isBusinessOpen ?? false);
    setApplicationPeriod(
      seasonData.applicationPeriod ?? ApplicationPeriod.ApplicationClosed,
    );
    setFreeDeliveryThreshold(Number(seasonData.freeDeliveryThreshold ?? 1000));
    setBusinessDeliveryPrice(Number(seasonData.businessDeliveryPrice ?? 80));
    setCSADeliveryPrice(Number(seasonData.csaDeliveryPrice ?? 80));
    setCheckboxes({ ...initialCheckboxes, ...seasonData.checkboxes });
    setWaitlist(seasonData.waitlist ?? []);
  };

  const resetSeasonData = (): void => {
    setSeason(null);
    setAreWeInSeason(false);
    setIsBusinessOpen(false);
    setApplicationPeriod(ApplicationPeriod.ApplicationClosed);
    setFreeDeliveryThreshold(1000);
    setBusinessDeliveryPrice(80);
    setCSADeliveryPrice(80);
    setCheckboxes(initialCheckboxes);
    setWaitlist([]);
  };

  const joinWaitlist = async (): Promise<boolean> => {
    const { waitlist: updatedWaitlist, alreadyOnWaitlist } =
      await callJoinSeasonWaitlist();
    setWaitlist(updatedWaitlist);
    if (season) {
      setSeason({ ...season, waitlist: updatedWaitlist });
    }
    return alreadyOnWaitlist;
  };

  const updateAreWeInSeason = async (
    nextAreWeInSeason: boolean,
  ): Promise<boolean> => {
    if (!season?.id) return false;
    const nextCheckboxes = nextAreWeInSeason
      ? initialCheckboxes
      : season.checkboxes;
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason: nextAreWeInSeason,
        isBusinessOpen,
        applicationPeriod,
        freeDeliveryThreshold,
        businessDeliveryPrice,
        csaDeliveryPrice,
        checkboxes: nextCheckboxes,
      });
      applySeasonData(updatedSeason);
      return true;
    } catch (err) {
      console.error("Error updating season:", err);
      setError(err instanceof Error ? err.message : "Failed to update season");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleCheckbox = async (item: SeasonCheckboxKey): Promise<void> => {
    if (!season?.id) return;
    const updatedCheckboxes = { ...checkboxes, [item]: !checkboxes[item] };
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason,
        isBusinessOpen,
        applicationPeriod,
        freeDeliveryThreshold,
        businessDeliveryPrice,
        csaDeliveryPrice,
        checkboxes: updatedCheckboxes,
      });
      applySeasonData(updatedSeason);
    } catch (err) {
      console.error("Error updating season:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update season checkboxes",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationPeriod = async (
    period: ApplicationPeriod,
  ): Promise<void> => {
    if (!season?.id) return;
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason,
        isBusinessOpen,
        applicationPeriod: period,
        freeDeliveryThreshold,
        businessDeliveryPrice,
        csaDeliveryPrice,
        checkboxes,
      });
      applySeasonData(updatedSeason);
    } catch (err) {
      console.error("Error updating application period:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update application period",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateIsBusinessOpen = async (open: boolean): Promise<void> => {
    if (!season?.id) return;
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason,
        isBusinessOpen: open,
        applicationPeriod,
        freeDeliveryThreshold,
        businessDeliveryPrice,
        csaDeliveryPrice,
        checkboxes,
      });
      applySeasonData(updatedSeason);
    } catch (err) {
      console.error("Error updating business open state:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update business open state",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateShippingPricing = async (
    nextFreeDeliveryThreshold: number,
    nextBusinessDeliveryPrice: number,
  ): Promise<void> => {
    if (!season?.id) return;
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason,
        isBusinessOpen,
        applicationPeriod,
        freeDeliveryThreshold: nextFreeDeliveryThreshold,
        businessDeliveryPrice: nextBusinessDeliveryPrice,
        csaDeliveryPrice,
        checkboxes,
      });
      applySeasonData(updatedSeason);
    } catch (err) {
      console.error("Error updating shipping pricing:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update shipping pricing",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCSADeliveryPrice = async (
    nextCSADeliveryPrice: number,
  ): Promise<void> => {
    if (!season?.id) return;
    try {
      setLoading(true);
      setError(null);
      const updatedSeason = await callUpdateSeason(season.id, {
        areWeInSeason,
        isBusinessOpen,
        applicationPeriod,
        freeDeliveryThreshold,
        businessDeliveryPrice,
        csaDeliveryPrice: nextCSADeliveryPrice,
        checkboxes,
      });
      applySeasonData(updatedSeason);
    } catch (err) {
      console.error("Error updating CSA delivery pricing:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update CSA delivery pricing",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadSeason = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        const seasonData = await callFetchSeason();
        applySeasonData(seasonData);
      } catch (err) {
        console.error("Error fetching season:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch season");
        resetSeasonData();
      } finally {
        setLoading(false);
      }
    };

    loadSeason();
  }, []);

  const effectiveAreWeInSeason =
    seasonPreviewOverride === null ? areWeInSeason : seasonPreviewOverride; //PREVIEW only
  const effectiveApplicationPeriod =
    applicationPeriodPreviewOverride === null
      ? applicationPeriod
      : applicationPeriodPreviewOverride; //PREVIEW only
  const value: SeasonContextType = {
    loading,
    error,
    areWeInSeason: effectiveAreWeInSeason, //PREVIEW only
    seasonPreviewOverride, //PREVIEW only
    applicationPeriod: effectiveApplicationPeriod, //PREVIEW only
    applicationPeriodPreviewOverride, //PREVIEW only
    freeDeliveryThreshold,
    businessDeliveryPrice,
    csaDeliveryPrice,
    checkboxes,
    waitlist,
    isBusinessOpen,
    joinWaitlist,
    updateAreWeInSeason,
    updateIsBusinessOpen,
    updateApplicationPeriod,
    updateShippingPricing,
    updateCSADeliveryPrice,
    toggleCheckbox,
    setSeasonPreviewOverride, //PREVIEW only
    clearSeasonPreviewOverride: () => setSeasonPreviewOverride(null), //PREVIEW only
    setApplicationPeriodPreviewOverride, //PREVIEW only
    clearApplicationPeriodPreviewOverride: () =>
      setApplicationPeriodPreviewOverride(null), //PREVIEW only
  };

  return (
    <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
  );
};
